<template>
  <div class="border border-slate-200 bg-white p-4 shadow-sm">
    <h3 class="mb-3 text-base font-semibold text-slate-800">
      {{
        sensorType === 'temperature'
          ? t('sensor.alarm_detail_chart.title_temperature')
          : sensorType === 'door_open'
            ? t('sensor.alarm_detail_chart.title_door_open')
            : isVoltageAlarm
              ? t('sensor.alarm_detail_chart.title_voltage')
              : t('sensor.alarm_detail_chart.title_electricity')
      }}
    </h3>
    <div
      v-if="sensorType === 'door_open'"
      class="mb-2 flex flex-wrap items-center gap-4 text-xs text-slate-500">
      <span class="inline-flex items-center gap-1.5">
        <span class="inline-block h-3 w-4 bg-yellow-400" />
        {{ t('sensor.alarm_detail_chart.state_closed') }}
      </span>
      <span class="inline-flex items-center gap-1.5">
        <span class="inline-block h-3 w-4 bg-red" />
        {{ t('sensor.alarm_detail_chart.state_open') }}
      </span>
    </div>
    <div ref="chartContainer" class="h-80 w-full min-h-[20rem]" />
    <p v-if="isLoading && !hasData" class="mt-2 text-center text-sm text-slate-500">
      {{ t('sensor.alarm_detail_chart.loading') }}
    </p>
    <p v-else-if="!isLoading && !hasData" class="mt-2 text-center text-sm text-slate-500">
      {{ t('sensor.alarm_detail_chart.no_data') }}
    </p>
  </div>
</template>

<script setup lang="ts">
import SensorResource from '@/api/resources/sensor'
import {
  mapDoorOpenVoltageToOpenPeriods,
  parseTimeDoorOpen,
  type IDoorOpenPeriod,
} from '@/utils/door-open-periods'
import {
  buildAlarmMarkArea,
  buildAlarmMarkLine,
} from '@/utils/components/alarm-detail'
import { buildCurrentChartOption } from '@/utils/components/alarm-detail/current-chart-options'
import { buildDoorOpenEchartsStateTimelineOption } from '@/utils/components/door-open-chart'
import { buildTemperatureChartOption } from '@/utils/components/alarm-detail/temperature-chart-options'
import { buildVoltageChartOption } from '@/utils/components/alarm-detail/voltage-chart-options'
import type { EChartsOption } from 'echarts'
import { normalizeEnergyReadingsForChart } from '@/utils/energy-readings-normalize'
import { normalizeVoltageReadingsForChart } from '@/utils/voltage-readings-normalize'
import { ALARM_CHART_TYPE_MAP, type AlarmChartDataType } from '@/utils/alarm-chart-type-map'
import { useI18n } from '@/composable/useI18n'
import * as echarts from 'echarts'
import { computed, nextTick, onBeforeUnmount, onMounted, ref, watch } from 'vue'

const { t } = useI18n()

const props = defineProps<{
  sensorId: number
  sensorType: 'electricity' | 'temperature' | 'door_open'
  alarmType: string
  startDate: string
  endDate: string | null
  alarmId: number
}>()

const chartContainer = ref<HTMLDivElement | null>(null)
let chartInstance: echarts.ECharts | null = null
const isLoading = ref(false)

// Energy
const seriesCurrentA = ref<[number, number][]>([])
const seriesCurrentB = ref<[number, number][]>([])
const seriesCurrentC = ref<[number, number][]>([])
const seriesAverage = ref<[number, number][]>([])

// Voltage
const seriesVoltageA = ref<[number, number][]>([])
const seriesVoltageB = ref<[number, number][]>([])
const seriesVoltageC = ref<[number, number][]>([])
const fixedVoltages = ref<Record<string, number>>({})

// Temperature
const chartData = ref<[number, number][]>([])

// Door open (State Timeline)
const doorOpenPeriods = ref<IDoorOpenPeriod[]>([])
const doorOpenChartRange = ref<{ start: number; end: number } | null>(null)
const doorOpenHasFetched = ref(false)

const alarmPeriod = computed<[number, number]>(() => {
  const start = new Date(props.startDate).getTime()
  const end = props.endDate ? new Date(props.endDate).getTime() : Date.now()
  return [start, end]
})

const savedDataZoom = ref({ start: 0, end: 100 })

/** Tipo de dado do gráfico derivado do tipo de alarme e do sensor (padrão extensível). */
const chartDataType = computed<AlarmChartDataType>(() => {
  if (props.sensorType === 'temperature') return 'temperature'
  if (props.sensorType === 'door_open') return 'door_open'
  return ALARM_CHART_TYPE_MAP[props.alarmType] ?? 'current'
})

const isVoltageAlarm = computed(() => chartDataType.value === 'voltage')

const hasData = computed(() => {
  if (props.sensorType === 'temperature') {
    return chartData.value.length > 0
  }
  if (props.sensorType === 'door_open') {
    return doorOpenHasFetched.value && doorOpenChartRange.value != null
  }
  if (isVoltageAlarm.value) {
    return (
      seriesVoltageA.value.length > 0 ||
      seriesVoltageB.value.length > 0 ||
      seriesVoltageC.value.length > 0 ||
      Object.keys(fixedVoltages.value).length > 0
    )
  }
  return (
    seriesCurrentA.value.length > 0 ||
    seriesCurrentB.value.length > 0 ||
    seriesCurrentC.value.length > 0 ||
    seriesAverage.value.length > 0
  )
})

/** Padding em ms (1 hora antes e depois) */
const PADDING_MS = 60 * 60 * 1000

async function fetchReadings() {
  if (!props.sensorId || !props.startDate) return
  isLoading.value = true
  const start = new Date(props.startDate).getTime() - PADDING_MS
  const end = props.endDate ? new Date(props.endDate).getTime() + PADDING_MS : Date.now() + PADDING_MS
  const startISO = new Date(start).toISOString()
  const endISO = new Date(end).toISOString()
  const dateRange = `start_date=${encodeURIComponent(startISO)}&end_date=${encodeURIComponent(endISO)}`

  try {
    if (props.sensorType === 'temperature') {
      const res = await SensorResource.getTemperatureSensorReadings({
        sensorId: props.sensorId,
        dateRange,
        resolution: '1m',
      })
      const readings = res?.readings
      const raw = Array.isArray(readings)
        ? readings
        : readings && typeof readings === 'object' && Array.isArray((readings as any).temperature)
          ? (readings as any).temperature
          : readings && typeof readings === 'object' && Array.isArray((readings as any).Temperatura)
            ? (readings as any).Temperatura
            : []
      chartData.value =
        raw.length > 0
          ? raw
              .map((r: { t?: string; v?: number; time?: string; value?: number }) => {
                const timeStr = r.t ?? r.time
                const val =
                  typeof (r.v ?? r.value) === 'number' && !Number.isNaN(Number(r.v ?? r.value))
                    ? Number(r.v ?? r.value)
                    : null
                if (!timeStr || val === null) return [0, 0]
                return [new Date(timeStr).getTime(), val]
              })
              .filter(([t]) => t > 0)
          : []
    } else if (props.sensorType === 'electricity') {
      if (isVoltageAlarm.value) {
        const res = await SensorResource.getEnergyVoltagePowerFactorReadings({
          sensorId: props.sensorId,
          dateRange,
          resolution: '1m',
          aggregation: 'mean',
        })
        const raw = res?.readings
        const norm = normalizeVoltageReadingsForChart(
          raw as Record<string, Array<{ t?: string; v?: number }>> | undefined,
          res?.fixed_values ?? {},
        )
        seriesVoltageA.value = norm.voltage_a
        seriesVoltageB.value = norm.voltage_b
        seriesVoltageC.value = norm.voltage_c
        fixedVoltages.value = norm.fixed
      } else {
        const res = await SensorResource.getEnergySensorReadings({
          sensorId: props.sensorId,
          dateRange,
          resolution: '1m',
          aggregation: 'mean',
        })
        const raw = res?.readings
        if (raw && typeof raw === 'object' && !Array.isArray(raw)) {
          const norm = normalizeEnergyReadingsForChart(raw as Record<string, Array<{ t?: string; v?: number }>>)
          seriesCurrentA.value = norm.current_a
          seriesCurrentB.value = norm.current_b
          seriesCurrentC.value = norm.current_c
          seriesAverage.value = norm.average
        } else {
          seriesCurrentA.value = []
          seriesCurrentB.value = []
          seriesCurrentC.value = []
          seriesAverage.value = []
        }
      }
    } else if (props.sensorType === 'door_open') {
      const res = await SensorResource.getOpenSensorReadings({
        sensorId: props.sensorId,
        dateRange,
        resolution: '1m',
      })
      const readings = res?.readings as Record<string, unknown> | undefined
      const fromTimeDoorOpen = parseTimeDoorOpen(readings)
      const voltageReadings = (readings?.door_open_voltage ?? []) as Array<{
        t?: string
        v?: number
        s?: string
      }>
      doorOpenPeriods.value =
        fromTimeDoorOpen.length > 0
          ? fromTimeDoorOpen
          : mapDoorOpenVoltageToOpenPeriods(voltageReadings, end)
      doorOpenChartRange.value = { start, end }
      doorOpenHasFetched.value = true
      savedDataZoom.value = { start: 0, end: 100 }
    }
  } catch {
    if (props.sensorType === 'temperature') {
      chartData.value = []
    } else if (props.sensorType === 'door_open') {
      doorOpenPeriods.value = []
      doorOpenChartRange.value = null
      doorOpenHasFetched.value = true
    } else if (isVoltageAlarm.value) {
      seriesVoltageA.value = []
      seriesVoltageB.value = []
      seriesVoltageC.value = []
      fixedVoltages.value = {}
    } else {
      seriesCurrentA.value = []
      seriesCurrentB.value = []
      seriesCurrentC.value = []
      seriesAverage.value = []
    }
  } finally {
    isLoading.value = false
  }
}

function initChart() {
  if (!chartContainer.value) return
  if (chartInstance) {
    chartInstance.dispose()
    chartInstance = null
  }
  chartInstance = echarts.init(chartContainer.value)
  updateChart()
}

function updateChart() {
  if (!chartInstance) return

  if (props.sensorType === 'door_open') {
    const range = doorOpenChartRange.value
    if (!range || range.end <= range.start) {
      chartInstance.clear()
      return
    }

    const openPeriods = doorOpenPeriods.value.map((period) => {
      const periodStart = new Date(period.start_date).getTime()
      const periodEnd = period.end_date ? new Date(period.end_date).getTime() : range.end
      return { y: [periodStart, periodEnd] as [number, number] }
    })

    const option = buildDoorOpenEchartsStateTimelineOption({
      rowLabel: t('sensor.alarm_detail_chart.chart_row_label'),
      openPeriods,
      rangeStart: range.start,
      rangeEnd: range.end,
      alarmPeriods: [alarmPeriod.value],
      hoveredAlarmIndex: 0,
      dataZoom: savedDataZoom.value,
    })

    chartInstance.setOption(option, true)
    return
  }

  const markArea = buildAlarmMarkArea(alarmPeriod.value)
  const markLine = buildAlarmMarkLine(alarmPeriod.value)

  let option: EChartsOption
  if (props.sensorType === 'temperature') {
    option = buildTemperatureChartOption({
      chartData: chartData.value,
      dataZoom: savedDataZoom.value,
      markArea,
      markLine,
      gridTop: '10%',
      gridLeft: '3%',
      gridRight: '3%',
      sliderBottom: 8,
    })
  } else if (isVoltageAlarm.value) {
    option = buildVoltageChartOption({
      seriesVoltageA: seriesVoltageA.value,
      seriesVoltageB: seriesVoltageB.value,
      seriesVoltageC: seriesVoltageC.value,
      fixedVoltages: fixedVoltages.value,
      phaseNames: [
        t('sensor.console.series.voltage_a'),
        t('sensor.console.series.voltage_b'),
        t('sensor.console.series.voltage_c'),
      ],
      alarmMarkArea: markArea,
      dataZoom: savedDataZoom.value,
      sliderBottom: 8,
    })
  } else {
    option = buildCurrentChartOption({
      seriesCurrentA: seriesCurrentA.value,
      seriesCurrentB: seriesCurrentB.value,
      seriesCurrentC: seriesCurrentC.value,
      seriesAverage: seriesAverage.value,
      alarmMarkArea: markArea,
      alarmMarkLine: markLine,
      dataZoom: savedDataZoom.value,
      sliderBottom: 8,
    })
  }

  chartInstance.setOption(option)
}

watch(
  () => [
    chartData.value,
    doorOpenPeriods.value,
    doorOpenChartRange.value,
    seriesCurrentA.value,
    seriesCurrentB.value,
    seriesCurrentC.value,
    seriesAverage.value,
    seriesVoltageA.value,
    seriesVoltageB.value,
    seriesVoltageC.value,
    fixedVoltages.value,
    alarmPeriod.value,
  ],
  () => nextTick(() => updateChart()),
  { deep: true },
)

onMounted(() => {
  fetchReadings().then(() => nextTick(() => initChart()))
  window.addEventListener('resize', () => chartInstance?.resize())
})

watch(
  () => [props.sensorId, props.startDate, props.endDate, props.sensorType, props.alarmType],
  () => {
    fetchReadings().then(() => nextTick(() => updateChart()))
  },
  { deep: true },
)

onBeforeUnmount(() => {
  window.removeEventListener('resize', () => chartInstance?.resize())
  if (chartInstance) {
    chartInstance.dispose()
    chartInstance = null
  }
})
</script>
